import { useNavigate } from 'react-router-dom';
import { Button, DialogShell, cx } from '@timely/ui';
import type { TenantContext } from '@timely/domain';
import { useWorkspaces } from './WorkspaceProvider';
import { CompanyMark } from '../branding/CompanyMark';

/** What a switch leaves on the way in, so the company's first screen can say where it arrived. */
export interface SwitchedState { switchedCompany: true }

/**
 * "Switch company": the companies this person belongs to, and a way into each.
 *
 * The list is the membership the shell already holds (WorkspaceProvider),
 * which the database answers with this person's own active memberships and
 * nothing else. Choosing one moves there in place, with no page load. The
 * address is resolved again on arrival — any company this person is not in
 * is "You do not have access" (RequireWorkspace) — and every read after that
 * is checked by the database, so neither this list nor an edited address
 * reaches a company that is not theirs. The company's screens start afresh
 * (RequireWorkspace keys them by company), so nothing of the company being
 * left is shown under the one being entered.
 *
 * Until 2026-10-10 "Switch" was a link to `/`, which loads the company last
 * used: the one being left. It reloaded the page and switched nothing.
 */
export function CompanySwitcher({ contexts, currentSlug, onClose }: {
    contexts: TenantContext[];
    currentSlug: string;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const { rememberSlug } = useWorkspaces();
    const companies = contexts.filter(c => c.membership.status === 'active');

    const choose = (slug: string) => {
        onClose();
        if (slug === currentSlug) return;
        rememberSlug(slug);
        // Today is a company's front page. An account or a setting open now belongs to the company being left.
        navigate(`/w/${slug}/today`, { state: { switchedCompany: true } satisfies SwitchedState });
    };

    // Up and Down move between the companies, Home and End to the first and the last, as in any list.
    const onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
        const buttons = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('button'));
        const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const next = e.key === 'ArrowDown' ? at + 1 : e.key === 'ArrowUp' ? at - 1
            : e.key === 'Home' ? 0 : e.key === 'End' ? buttons.length - 1 : null;
        if (next === null || buttons.length === 0) return;
        e.preventDefault();
        buttons[(next + buttons.length) % buttons.length]!.focus();
    };

    return (
        <DialogShell
            title="Switch company"
            subtitle="The companies you belong to."
            size="sm"
            flush
            onClose={onClose}
            footer={<Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>}
        >
            <ul aria-label="Your companies" className="divide-y divide-separator" onKeyDown={onKeyDown}>
                {companies.map(c => {
                    const current = c.workspace.slug === currentSlug;
                    return (
                        <li key={c.workspace.id}>
                            <button
                                type="button"
                                onClick={() => choose(c.workspace.slug)}
                                aria-current={current ? 'true' : undefined}
                                // Focus starts on the company you are in, as a list starts on its chosen item.
                                data-autofocus={current ? true : undefined}
                                className={cx(
                                    'w-full text-left flex items-center gap-3 px-6 py-3 min-h-14 max-md:px-4 transition-colors',
                                    'hover:bg-hover focus-visible:bg-hover',
                                    current && 'bg-accent-tint',
                                )}
                            >
                                <CompanyMark name={c.workspace.name} logoPath={c.workspace.logoPath ?? null} />
                                <span className="min-w-0 flex-1">
                                    <span className="block font-semibold text-label truncate">{c.workspace.name}</span>
                                    <span className="block text-secondary text-label-3">
                                        {c.membership.roleName}{current ? ' · Current' : ''}
                                    </span>
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </DialogShell>
    );
}
